<template>
  <div class="">
    <label :for="id" class="file-upload-label">{{ label }}</label>
    <div class="upload-button-container mb-1">
      <b-button
        v-if="showExecuteButton"
        variant="primary"
        size="sm"
        :disabled="commandDisabled"
        class="upload-button"
        @click="onClick"
      >
        {{ $t("action_plan.viewer.execute_command") }} {{ getNameOfCommand() }}
      </b-button>

      <div v-if="isTracking" class="d-flex align-items-center text-info">
        <b-spinner small class="mr-50"></b-spinner>
        <span>{{ trackingStatusText }}</span>
      </div>

      <div v-else-if="executionResult" class="d-flex flex-column align-items-start">
        <span :class="resultTextClass">
          <feather-icon :icon="resultIcon" size="18" class="mr-25"/>
          {{ resultStatusText }}: {{ executionMessage }}
        </span>
        <small v-if="executionDate" class="text-muted mt-25">
          {{ executionDate }}
        </small>
        <b-button
          v-if="canRetry"
          variant="outline-danger"
          size="sm"
          class="mt-50"
          :disabled="commandDisabled"
          @click="retryCommand"
        >
          {{ $t("operational_security.evaluation.command_retry") }}
        </b-button>
      </div>

      <div v-if="trackingError" class="d-flex flex-column align-items-start text-warning mt-50">
        <span>
          <feather-icon icon="AlertTriangleIcon" size="18" class="mr-25"/>
          {{ trackingError }}
        </span>
        <b-button
          v-if="operationId"
          variant="outline-warning"
          size="sm"
          class="mt-50"
          :disabled="commandDisabled"
          @click="resumeTracking"
        >
          {{ $t("operational_security.evaluation.command_check_result") }}
        </b-button>
      </div>
    </div>
    <div class="d-flex flex-column">
      <span class="text-warning" v-if="!facilityId">
        <feather-icon icon="AlertTriangleIcon" size="18" class="mr-25"/>
        {{ $t("operational_security.select_facility_to_enable_command") }}
      </span>
      <span class="text-warning" v-if="!formId">
        <feather-icon icon="AlertTriangleIcon" size="18" class="mr-25"/>
        {{ $t("operational_security.form_id_to_enable_command") }}
      </span>
    </div>
  </div>
</template>

<script>
import {
  computed,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "@vue/composition-api";
import { BSpinner } from 'bootstrap-vue'
import axios from "@axios";
import moment from "moment";
import Swal from "sweetalert2";
import { EventBus } from "@/libs/event-bus";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 120000;
const TERMINAL_STATUSES = ["succeeded", "failed", "dispatched", "completed"];

export default {
  components: {
    BSpinner,
  },
  props: {
    id: String,
    name: String,
    label: String,
    fieldOptions: Object,
    disabled: {
      type: Boolean,
      default: false,
    },
    context: Object
  },
  setup(props) {
    const vm = getCurrentInstance().proxy;
    const executionResult = ref(null);
    const isCommandLoading = ref(false);
    const isBtnDisabled = ref(false);
    const isRetryConfirmationOpen = ref(false);
    const operationId = ref(null);
    const trackingError = ref(null);
    const trackingStatus = ref(null);
    let destroyed = false;
    let pollGeneration = 0;
    let pollTimer = null;
    let resolvePollDelay = null;

    const facilityId = computed(() => {
      if (props?.fieldOptions?.facilityId !== undefined) {
        return props.fieldOptions.facilityId
      }

      return props?.fieldOptions?.lastFacilitySelected?.facility?.value
    });
    const formId = computed(() => {
      return props?.fieldOptions?.formId
    });
    const isTracking = computed(() => {
      return isCommandLoading.value
        && ["queued", "running"].includes(trackingStatus.value);
    });
    const showExecuteButton = computed(() => {
      return !executionResult.value && !isTracking.value && !trackingError.value;
    });
    const commandDisabled = computed(() => {
      return props.disabled
        || isBtnDisabled.value
        || isCommandLoading.value
        || isRetryConfirmationOpen.value
        || !facilityId.value
        || !formId.value;
    });
    const canRetry = computed(() => {
      return executionResult.value?.status === "failed" && !isCommandLoading.value;
    });
    const executionMessage = computed(() => {
      return executionResult.value?.message
        || vm.$t("operational_security.evaluation.command_executed");
    });
    const executionDate = computed(() => {
      const value = executionResult.value?.executed_at;
      if (!value) return "";

      const date = moment(value);
      return date.isValid() ? date.format("YYYY-MM-DD HH:mm:ss") : value;
    });
    const resultStatusText = computed(() => {
      const key = executionResult.value?.status === "failed"
        ? "command_failed"
        : "command_succeeded";

      return vm.$t(`operational_security.evaluation.${key}`);
    });
    const resultTextClass = computed(() => {
      return executionResult.value?.status === "failed" ? "text-danger" : "text-success";
    });
    const resultIcon = computed(() => {
      return executionResult.value?.status === "failed"
        ? "XCircleIcon"
        : "CheckCircleIcon";
    });
    const trackingStatusText = computed(() => {
      const key = trackingStatus.value === "running"
        ? "command_running"
        : "command_queued";

      return vm.$t(`operational_security.evaluation.${key}`);
    });

    onMounted(() => {
      isBtnDisabled.value = props.context.attributes.disabled ?? false;
    });

    onBeforeUnmount(() => {
      destroyed = true;
      pollGeneration += 1;
      if (pollTimer) clearTimeout(pollTimer);
      if (resolvePollDelay) resolvePollDelay();
    });

    watch(
      () => props.context.attributes.disabled,
      (newValue, oldValue) => {
        isBtnDisabled.value = props.context.attributes.disabled ?? false;
      },
      { immediate: true }
    );

    watch(
      () => props?.context?.model,
      (value) => {
        if (isCommandLoading.value) return;
        executionResult.value = normalizeStoredResult(value);
        operationId.value = executionResult.value?.operation_id || null;
      },
      { immediate: true, deep: true }
    );

    function getNameOfCommand() {
      return props?.fieldOptions?.fieldTypeCfg?.data[0]?.name || ''
    }

    function normalizeStoredResult(value) {
      if (!value) return null;

      if (typeof value === "object" && value.status) {
        return {
          ...value,
          status: value.status === "completed" ? "succeeded" : value.status,
        };
      }

      return {
        label: String(value),
        status: "succeeded",
        code: null,
        message: vm.$t("operational_security.evaluation.command_executed"),
        executed_at: null,
        operation_id: null,
        legacy: true,
      };
    }

    function unwrapResponse(response) {
      return response?.data?.data ?? response?.data ?? {};
    }

    function waitForNextPoll() {
      return new Promise((resolve) => {
        resolvePollDelay = resolve;
        pollTimer = setTimeout(() => {
          pollTimer = null;
          resolvePollDelay = null;
          resolve();
        }, POLL_INTERVAL_MS);
      });
    }

    function normalizeTerminalResult(data) {
      const status = ["completed", "dispatched"].includes(data.status)
        ? "succeeded"
        : data.status;
      const message = data.message || vm.$t(
        `operational_security.evaluation.${status === "failed" ? "command_failed" : "command_succeeded"}`
      );

      return {
        label: message,
        status,
        code: data.code || null,
        message,
        executed_at: data.completed_at || moment().toISOString(),
        operation_id: data.operation_id || operationId.value,
      };
    }

    async function applyTerminalResult(data) {
      const result = normalizeTerminalResult(data);
      executionResult.value = result;
      operationId.value = result.operation_id;
      trackingStatus.value = null;
      trackingError.value = null;
      props.context.model = result;

      const onCommandFinished = props?.fieldOptions?.onCommandFinished;
      if (typeof onCommandFinished === "function") {
        await onCommandFinished({
          fieldName: props?.fieldOptions?.fieldName || props.name || props?.context?.name,
          result,
        });
      } else {
        EventBus.emit("operational_security_reload_forms_and_list");
      }
    }

    async function pollOperation(currentOperationId) {
      const generation = ++pollGeneration;
      const deadline = Date.now() + POLL_TIMEOUT_MS;

      while (!destroyed && generation === pollGeneration && Date.now() < deadline) {
        try {
          const response = await axios.get(`v1/form-action-runs/${currentOperationId}`);
          const data = unwrapResponse(response);

          trackingStatus.value = data.status || "queued";

          if (TERMINAL_STATUSES.includes(data.status)) {
            await applyTerminalResult(data);
            return;
          }
        } catch (error) {
          // Transient tracking failures are retried until the polling deadline.
        }

        await waitForNextPoll();
      }

      if (!destroyed && generation === pollGeneration) {
        trackingStatus.value = null;
        trackingError.value = vm.$t(
          "operational_security.evaluation.command_tracking_timeout"
        );
      }
    }

    async function executeCommand() {
      if (commandDisabled.value) return;

      executionResult.value = null;
      operationId.value = null;
      isCommandLoading.value = true;
      trackingError.value = null;
      trackingStatus.value = "queued";

      const commandId = props?.fieldOptions?.fieldTypeCfg?.data[0]?.id
      const payload = {
        id_form: formId.value,
        id_facility: facilityId.value,
      }

      try {
        const response = await axios.post(`v1/form-actions/${commandId}`, {payload});
        const data = unwrapResponse(response);
        operationId.value = data.operation_id || null;

        if (TERMINAL_STATUSES.includes(data.status)) {
          await applyTerminalResult(data);
          return;
        }

        if (!operationId.value) {
          throw new Error("The command operation ID was not returned.");
        }

        trackingStatus.value = data.status || "queued";
        await pollOperation(operationId.value);
      } catch (error) {
        if (!trackingError.value) {
          trackingStatus.value = null;
          trackingError.value = vm.$t(
            "operational_security.evaluation.command_tracking_error"
          );
        }
      } finally {
        if (!destroyed) isCommandLoading.value = false;
      }
    }

    async function onClick() {
      await executeCommand();
    }

    async function retryCommand() {
      if (!canRetry.value || commandDisabled.value) return;

      isRetryConfirmationOpen.value = true;
      let confirmation;

      try {
        confirmation = await Swal.fire({
          title: vm.$t("operational_security.evaluation.command_retry_title"),
          text: vm.$t("operational_security.evaluation.command_retry_warning"),
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: vm.$t("actions.accept"),
          cancelButtonText: vm.$t("actions.cancel"),
          customClass: {
            confirmButton: "btn btn-primary text-capitalize",
            cancelButton: "btn btn-outline-danger ml-1",
          },
          buttonsStyling: false,
        });
      } finally {
        isRetryConfirmationOpen.value = false;
      }

      if (!confirmation?.isConfirmed) return;

      await executeCommand();
    }

    async function resumeTracking() {
      if (!operationId.value || commandDisabled.value) return;

      isCommandLoading.value = true;
      trackingError.value = null;
      trackingStatus.value = "queued";

      try {
        await pollOperation(operationId.value);
      } finally {
        if (!destroyed) isCommandLoading.value = false;
      }
    }

    return {
      canRetry,
      commandDisabled,
      executionDate,
      executionMessage,
      executionResult,
      facilityId,
      formId,
      getNameOfCommand,
      isTracking,
      onClick,
      operationId,
      resultIcon,
      resultStatusText,
      resultTextClass,
      resumeTracking,
      retryCommand,
      showExecuteButton,
      trackingError,
      trackingStatusText,
    }
  }
}
</script>
